import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { Container } from "../components/uikit";
import { useCRT } from "../hooks";

interface CRTRoute {
  path: string;
  element: ReactNode;
}

interface CRTPagesProps {
  routes: CRTRoute[];
  /** Duration of page transitions in milliseconds */
  transitionDuration?: number;
  /** Animation style for transitions */
  transitionStyle?: "cut" | "flash" | "fade";
  /** Footer element that persists across pages */
  footer?: ReactNode;
}

type TransitionPhase = "idle" | "fadeOut" | "fadeIn";

interface TransitionState {
  phase: TransitionPhase;
  progress: number;
  currentElement: ReactNode | null;
  pendingElement: ReactNode | null;
}

function easeOutQuad(x: number): number {
  return 1 - (1 - x) * (1 - x);
}

function easeInQuad(x: number): number {
  return x * x;
}

export function CRTPages({
  routes,
  transitionDuration = 200,
  transitionStyle = "flash",
  footer,
}: CRTPagesProps) {
  const { width, height } = useCRT();
  const location = useLocation();

  const [state, setState] = useState<TransitionState>({
    phase: "idle",
    progress: 0,
    currentElement: null,
    pendingElement: null,
  });

  const animationRef = useRef<number | null>(null);
  const transitionStartRef = useRef<number>(0);
  const prevPathRef = useRef<string>(location.pathname);

  // Find element for a given path
  const findRouteElement = useCallback(
    (path: string): ReactNode | null => {
      const route = routes.find((r) => {
        if (r.path === path) return true;
        // Handle wildcard paths like /projects/*
        if (r.path.endsWith("*")) {
          const basePath = r.path.slice(0, -1);
          return path.startsWith(basePath);
        }
        // Handle dynamic routes like /projects/:id
        if (r.path.includes(":")) {
          const pathParts = path.split("/");
          const routeParts = r.path.split("/");
          if (pathParts.length !== routeParts.length) return false;
          return routeParts.every(
            (part, i) => part.startsWith(":") || part === pathParts[i],
          );
        }
        return false;
      });
      return route?.element || null;
    },
    [routes],
  );

  const currentRouteElement = useMemo(
    () => findRouteElement(location.pathname),
    [findRouteElement, location.pathname],
  );

  // Initialize on mount
  useEffect(() => {
    if (!state.currentElement && currentRouteElement) {
      setState((prev) => ({
        ...prev,
        currentElement: currentRouteElement,
      }));
    }
  }, [currentRouteElement, state.currentElement]);

  // Handle route changes with two-phase transition
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      const newElement = findRouteElement(location.pathname);
      prevPathRef.current = location.pathname;

      if (!newElement) return;

      // For "cut" style, immediately switch
      if (transitionStyle === "cut") {
        setState((prev) => ({
          ...prev,
          currentElement: newElement,
        }));
        return;
      }

      // For "flash" or "fade", start fade-out phase
      setState((prev) => ({
        ...prev,
        phase: "fadeOut",
        progress: 0,
        pendingElement: newElement,
      }));

      transitionStartRef.current = performance.now();
      const halfDuration = transitionDuration / 2;

      const animateFadeOut = () => {
        const elapsed = performance.now() - transitionStartRef.current;
        const rawProgress = Math.min(elapsed / halfDuration, 1);
        const progress = easeInQuad(rawProgress);

        setState((prev) => ({
          ...prev,
          progress,
        }));

        if (rawProgress < 1) {
          animationRef.current = requestAnimationFrame(animateFadeOut);
        } else {
          // Switch to fade-in phase
          transitionStartRef.current = performance.now();

          setState((prev) => ({
            ...prev,
            phase: "fadeIn",
            progress: 0,
            currentElement: prev.pendingElement,
            pendingElement: null,
          }));

          const animateFadeIn = () => {
            const elapsed = performance.now() - transitionStartRef.current;
            const rawProgress = Math.min(elapsed / halfDuration, 1);
            const progress = easeOutQuad(rawProgress);

            setState((prev) => ({
              ...prev,
              progress,
            }));

            if (rawProgress < 1) {
              animationRef.current = requestAnimationFrame(animateFadeIn);
            } else {
              // Transition complete
              setState((prev) => ({
                ...prev,
                phase: "idle",
                progress: 0,
              }));
            }
          };

          animationRef.current = requestAnimationFrame(animateFadeIn);
        }
      };

      animationRef.current = requestAnimationFrame(animateFadeOut);
    }
  }, [
    location.pathname,
    findRouteElement,
    transitionDuration,
    transitionStyle,
  ]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Calculate opacity based on transition phase and style
  const getContentOpacity = useCallback((): number => {
    const { phase, progress } = state;

    if (phase === "idle") return 1;

    if (transitionStyle === "flash") {
      // Flash style: quick fade to white/bright then back
      if (phase === "fadeOut") {
        return 1 - progress;
      }
      return progress;
    }

    // Fade style: smooth opacity transition
    if (phase === "fadeOut") {
      return 1 - progress;
    }
    return progress;
  }, [state, transitionStyle]);

  const contentOpacity = getContentOpacity();

  // Footer height for content padding
  const footerHeight = footer ? 80 : 0;

  console.log("current element", state.currentElement);

  return (
    <Container
      positionType="absolute"
      positionLeft={0}
      positionTop={0}
      width={width}
      height={height}
      flexDirection="column"
    >
      {/* Main content area */}
      <Container
        width={width}
        height={height - footerHeight}
        positionType="relative"
        overflow="hidden"
      >
        {/* Current page - only one page rendered at a time */}
        {state.currentElement && (
          <Container
            positionType="absolute"
            positionLeft={0}
            positionTop={0}
            width={width}
            height={height - footerHeight}
            opacity={contentOpacity}
          >
            {state.currentElement}
          </Container>
        )}
      </Container>

      {/* Persistent footer - always fully visible */}
      {footer && (
        <Container
          width={width}
          height={footerHeight}
          positionType="absolute"
          positionBottom={0}
          positionLeft={0}
        >
          {footer}
        </Container>
      )}
    </Container>
  );
}
